import { BitkitAlert, BitkitAlertProps, BitkitLink } from '@bitrise/bitkit-v2';
import { format } from 'date-fns';
import { ReactNode } from 'react';

import URLs from '@/enums/urls';

import { WorkspaceSettingsPage } from '../../../../enums/WorkspaceSettingsPage';
import { workflowEditorStackPath, workspaceSettingsPath } from '../../../../utils/monolithApiRouteService';

const machineUpgradeAnnouncementUrl = 'https://docs.google.com/document/d/1DjttMcPHn4c76bVG6_jDQ4RfG08oLTMJ8rusHUA-0gE';

type DeprecatedMachineBannerProps = {
  workspaceSlug: string;
  appSlug: string;
  canEditWorkflow: boolean;
  canChangeWorkspaceSettings: boolean;
  isGracePeriodRequested: boolean;
  gracePeriodStartedAt: Date;
  gracePeriodEndedAt: Date;
};

const getMessage = (
  isGracePeriodRequested: boolean,
  gracePeriodStartedAt: Date,
  gracePeriodEndedAt: Date,
): ReactNode | string => {
  const currentDate = new Date();

  if (currentDate < gracePeriodStartedAt) {
    return (
      <>
        Your Linux machines will automatically switch to newer generation AMD EPYC Zen4 ones, powered by more performant
        CPU generation, on the {format(gracePeriodStartedAt, "do 'of' MMMM")}.{' '}
        <BitkitLink
          aria-label="Read announcement"
          href={machineUpgradeAnnouncementUrl}
          textDecoration="underline"
          isExternal
        >
          Read announcement
        </BitkitLink>
      </>
    );
  }

  if (currentDate >= gracePeriodStartedAt && currentDate <= gracePeriodEndedAt) {
    if (isGracePeriodRequested) {
      return `Your Workspace entered the grace period to extend using the old Linux machines. You can keep using the older Standard machine until the ${format(
        gracePeriodEndedAt,
        "do 'of' MMMM",
      )}.`;
    }

    return `Your Standard Linux machine was automatically switched to newer generation machine type, powered by more performant AMD EPYC Zen4 hardware. You can keep using the older Linux machines until the ${format(gracePeriodEndedAt, "do 'of' MMMM")}.`;
  }

  return (
    <>
      Please select a newer generation AMD EPYC Zen4 Linux machine type instead of the Standard machine. If you'd like
      to keep using the Standard machine, reach out to{' '}
      <BitkitLink aria-label="support." href={URLs.SUPPORT_CENTER} textDecoration="underline" isExternal>
        support.
      </BitkitLink>
    </>
  );
};

const DeprecatedMachineBanner = ({
  workspaceSlug,
  appSlug,
  canEditWorkflow,
  canChangeWorkspaceSettings,
  isGracePeriodRequested,
  gracePeriodStartedAt,
  gracePeriodEndedAt,
}: DeprecatedMachineBannerProps) => {
  const message = getMessage(isGracePeriodRequested, gracePeriodStartedAt, gracePeriodEndedAt);

  let action: BitkitAlertProps['action'];
  if (
    new Date() >= gracePeriodStartedAt &&
    new Date() <= gracePeriodEndedAt &&
    !isGracePeriodRequested &&
    canChangeWorkspaceSettings
  ) {
    action = {
      label: 'Extend transition period',
      href: workspaceSettingsPath(workspaceSlug, WorkspaceSettingsPage.APPS),
    };
  } else {
    action = canEditWorkflow ? { label: 'Manage machines', href: workflowEditorStackPath(appSlug) } : undefined;
  }

  return (
    <BitkitAlert
      variant="info"
      action={action}
      titleText="Upgrading to a newer generation Linux machine type"
      messageText={message}
    />
  );
};

export default DeprecatedMachineBanner;
